import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { CssBaseline, Toolbar } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { theme } from '../theme';
import ChangePassword from './Auth/ChangePassword';
import LockedAccount from './Auth/LockedAccount';
import Login from './Auth/Login';
import PasswordRecovery from './Auth/PasswordRecovery';
import PhysicianDashboardAuthRedirect from './Auth/PhysicianDashboardAuthRedirect';
import RequireAuth from './Auth/RequireAuth';
import RequirePhysicianDashboardAuth from './Auth/RequirePhysicianDashboardAuth';
import RequireStaffReviewAuth from './Auth/RequireStaffReviewAuth';
import RequireSuperAccess from './Auth/RequireSuperAccess';
import StaffReviewAuthRedirect from './Auth/StaffReviewAuthRedirect';
import DraftPreview from './Campaign/CampaignPreview/DraftPreview/DraftPreview';
import Campaigns from './Campaign/Campaigns';
import CreateCampaign from './Campaign/CreateCampaign';
import EditCampaign from './Campaign/EditCampaign';
import CardList from './Card/CardList';
import CardTemplate from './Card/CardTemplate';
import CreateCard from './Card/CreateCard/CreateCard';
import LandingPage from './Demo/LandingPage';
import Header from './Header';
import { AuthProvider } from './Hooks/useAuth';
import { UserContextProvider } from './Hooks/useUser';
import CreateItem from './Inventory/CreateItem';
import Inventory from './Inventory/Inventory';
import ItemDetails from './Inventory/ItemDetails';
import OrganizationConfiguration from './SuperSettings/Organizations/OrganizationConfiguration';
import OrganizationView from './SuperSettings/Organizations/OrganizationView';
import SuperView from './SuperSettings/Organizations/SuperView';
import EditPhysician from './Physician/EditPhysician';
import CardReview from './PhysicianDashboard/CardReview/CardReview';
import usePhysicianDashboardUser from './Hooks/usePhysicianDashboardUser';
import { useUpdateCardOwnerMutation } from '../api/physicianDashboardSlice';
import ComplianceSwaps from './PhysicianDashboard/ItemSwaps/Compliance/ComplianceSwaps';
import ItemSwaps from './PhysicianDashboard/ItemSwaps/ItemSwaps';
import PackList from './PhysicianDashboard/Packs/PackList';
import ItemReplacements from './PhysicianDashboard/ItemReplacements/ItemReplacements';
import PhysicianDashboard from './PhysicianDashboard/PhysicianDashboard';
import Sidebar from './Sidebar/Sidebar';
import StaffReviewSidebar from './Sidebar/StaffReviewSidebar';
import ManageCategories from './SuperSettings/Categories/ManageCategories';
import CreateHospital from './SuperSettings/Hospital/CreateHospital';
import UpdateHospitalSetup from './SuperSettings/Hospital/HospitalConfiguration';
import Ingestion from './SuperSettings/Ingestion/Ingestion';
import IngestionImport from './SuperSettings/Ingestion/IngestionImport';
import Conversation from './SuperSettings/PhysicianTexting/Conversation';
import PhysicianTexting from './SuperSettings/PhysicianTexting/PhysicianTexting';
import SuperSettings from './SuperSettings/SuperSettings';
import PasswordReset from './SuperSettings/UserManagement/PasswordReset';
import CreateUser from './User/CreateUser';
import EditUser from './User/EditUser';
import UsersView from './User/UsersView';
import CampaignLandingPage from './PhysicianDashboard/LandingPage/CampaignLandingPage';
import BulkTexting from './Campaign/CampaignTexting/BulkTexting';
import UserManagement from './SuperSettings/Organizations/UserManagement/UserManagement';
import CampaignMonitoring from './CampaignMonitoring/CampaignMonitoring';
import EmailUnsubscribe from './Auth/EmailUnsubscribe';
import StaffReviewLandingPage from './StaffReviewPortal/v2/StaffReviewLandingPage';
import StaffReviewCardView from './StaffReviewPortal/v2/StaffReviewCardView';
import CompletedReviews from './StaffReviewPortal/v2/CompletedReviews';
import ImplementerInbox from './ImplementerInbox/ImplementerInbox';
import ImplementerReview from './ImplementerInbox/ImplementerReview';
import ItemImageReview from './SuperSettings/ImageReview/ItemImageReview';
import PhoneNumberUpload from './SuperSettings/Organizations/PhysicianManagement/PhoneNumberUpload';
import UploadSourceFile from './SuperSettings/Organizations/FileUpload/UploadSourceFile';
import ItemAnalytics from './SuperSettings/Organizations/ItemAnalytics';
import PackReview from './PhysicianDashboard/Packs/PackReview';
import PeerComparison from './PhysicianDashboard/PeerComparison/PeerComparison';
import DataExplorationAgent from './SuperSettings/Assisstants/DataExplorationAgent';
import RecommendationBatchTracking from './SuperSettings/Organizations/RecommendationBatchTracking/RecommendationBatchTracking';
import PackMonitoring from './CampaignMonitoring/PackMonitoring';
import PackUsageManagement from './SuperSettings/Organizations/PackManagement/PackUsageManagement';
import ProcedureAnalytics from './Analytics/ProcedureAnalytics';
import Insights from './Inventory/Insights';
import { ApiContextProvider } from './Hooks/useApiContext';
import ProcedureAnalysis from './Procedures/ProcedureAnalysis';
import ReviewerAssignmentPage from './SuperSettings/StaffReviewManagement/ReviewerAssignment/ReviewerAssignmentPage';
import ProcedureInsights from './Procedures/ProcedureInsights';
import ProcedureItemAnalysis from './Procedures/ProcedureItemAnalysis';
import CreateContract from './ContractIntelligence/CreateContract';
import ContractIntelligence from './ContractIntelligence/Contracts';
import Contract from './ContractIntelligence/Contract';
import UncategorizedItemsReportUpload from './SuperSettings/Organizations/UncategorizedItemsReport/UncategorizedItemsReportUpload';
import Physicians from './Physician/Physicians';

function App() {

    return (
        <AuthProvider>
            <UserContextProvider>
                <ThemeProvider theme={theme}>
                    <Routes>
                        <Route path='/login' element={<Login />} />
                        <Route path='/recovery' element={<PasswordRecovery />} />
                        <Route path='/password/reset/:token' element={<ChangePassword />} />
                        <Route path='/lockedAccount' element={<LockedAccount />} />
                        <Route path='/demo/register' element={<LandingPage />} />
                        <Route path='/unsubscribe/:userId' element={<EmailUnsubscribe />} />
                        <Route path='/physicianDashboard/auth' element={<PhysicianDashboardAuthRedirect />} />
                        <Route path='/staffReview/auth' element={<StaffReviewAuthRedirect />} />
                        
                        <Route element={
                            <ApiContextProvider area="physician">
                                <DashboardLayout />
                            </ApiContextProvider>
                        }>
                            <Route path='/physician/drafts/:draftId' element={
                                <RequirePhysicianDashboardAuth>
                                    <CardReview />
                                </RequirePhysicianDashboardAuth>
                            }/>
                            <Route path='/physician/drafts/:draftId/comparison' element={
                                <RequirePhysicianDashboardAuth>
                                    <PeerComparison />
                                </RequirePhysicianDashboardAuth>
                            }/>
                            <Route path='/physician/dashboard' element={
                                <PhysicianDashboardAuthRedirect />
                            }/>
                            <Route path='/physicianDashboard/headline/:campaignId' element ={
                                <RequirePhysicianDashboardAuth>
                                    <PhysicianDashboard />
                                </RequirePhysicianDashboardAuth>
                            }/>
                            <Route path='/physician/:physicianId/swaps/:campaignId' element={
                                <RequirePhysicianDashboardAuth>
                                    <ItemSwaps />
                                </RequirePhysicianDashboardAuth>
                            } />
                            <Route path='/physician/:physicianId/compliance/:campaignId' element={
                                <RequirePhysicianDashboardAuth>
                                    <ComplianceSwaps />
                                </RequirePhysicianDashboardAuth>
                            } />
                            <Route path='/physician/:physicianId/replacements' element={
                                <RequirePhysicianDashboardAuth>
                                    <ItemReplacements />
                                </RequirePhysicianDashboardAuth>
                            } />
                            <Route path='/physician/:physicianId/packs' element={
                                <RequirePhysicianDashboardAuth>
                                    <PackList />
                                </RequirePhysicianDashboardAuth>
                            } />
                            <Route path='/physician/:physicianId/packs/:packReviewId' element={
                                <RequirePhysicianDashboardAuth>
                                    <PackReview />
                                </RequirePhysicianDashboardAuth>
                            } />
                        </Route>
                        <Route element={ 
                            <ApiContextProvider area="physician">
                                <LandingPageLayout />
                            </ApiContextProvider>
                        }>
                            <Route path='/physician/dashboard/landing' element={
                                <RequirePhysicianDashboardAuth>
                                    <CampaignLandingPage />
                                </RequirePhysicianDashboardAuth>
                            } />
                        </Route>
                        <Route element={
                            <ApiContextProvider area="staffReview">
                                <StaffReviewLayout />
                            </ApiContextProvider>
                        }>
                            <Route path='/staffReview/activeReviews' element={
                                <RequireStaffReviewAuth>
                                    <StaffReviewLandingPage />
                                </RequireStaffReviewAuth>
                            } />
                            <Route path='/staffReview/pastReviews' element={
                                <RequireStaffReviewAuth>
                                    <CompletedReviews />
                                </RequireStaffReviewAuth>
                            } />
                            <Route path='/staffReview/:draftId' element={
                                <RequireStaffReviewAuth>
                                    <StaffReviewCardView />
                                </RequireStaffReviewAuth>
                            } />
                        </Route>
                        <Route element={
                            <RequireAuth>
                                <ApiContextProvider area="admin">
                                    <Layout />
                                </ApiContextProvider>
                            </RequireAuth>
                        }>
                            <Route path='/cards' element={
                                <RequireAuth>
                                    <CardList />
                                </RequireAuth>
                            } />
                            <Route path='/cards/create' element={
                                <RequireAuth>
                                    <CreateCard />
                                </RequireAuth>
                            } />
                            <Route path='/cards/:cardID' element={
                                <RequireAuth>
                                    <CardTemplate />
                                </RequireAuth>
                            } />
                            <Route path='/inventory' element={
                                <RequireAuth>
                                    <Inventory />
                                </RequireAuth>
                            } />
                            <Route path='/inventory/:itemId/details' element={
                                <RequireAuth>
                                    <ItemDetails />
                                </RequireAuth>
                            } />
                            <Route path='/inventory/create' element={
                                <RequireAuth>
                                    <CreateItem />
                                </RequireAuth>
                            } />
                            <Route path='/inventory/insights' element={
                                <RequireAuth>
                                    <Insights />
                                </RequireAuth>
                            } />
                            <Route path='/organizations/:organizationId/items/:itemId/itemAnalytics' element={
                                <RequireAuth>
                                    <ItemAnalytics />
                                </RequireAuth>
                            } />
                            <Route path='/users' element={
                                <RequireAuth>
                                    <UsersView />
                                </RequireAuth>
                            } />
                            <Route path='/users/create' element={
                                <RequireAuth>
                                    <CreateUser />
                                </RequireAuth>
                            } />
                            <Route path='/users/:userId' element={
                                <RequireAuth>
                                    <EditUser />
                                </RequireAuth>
                            } />
                            <Route path='/campaigns' element={
                                <RequireSuperAccess>
                                    <Campaigns />
                                </RequireSuperAccess>
                            } />
                            <Route path='/campaigns/:campaignId' element={
                                <RequireSuperAccess>
                                    <EditCampaign />
                                </RequireSuperAccess>
                            } />
                            <Route path='/campaigns/:campaignId/texting' element={
                                <RequireSuperAccess>
                                    <BulkTexting />
                                </RequireSuperAccess>
                            } />
                            <Route path='/campaigns/:campaignId/previews/:cardId' element={
                                <RequireSuperAccess>
                                    <DraftPreview />
                                </RequireSuperAccess>
                            } />
                            <Route path='/campaigns/create' element={
                                <RequireSuperAccess>
                                    <CreateCampaign/>
                                </RequireSuperAccess>
                            } />
                            <Route path='/analytics' element={
                                <RequireAuth>
                                    <ProcedureAnalytics />
                                </RequireAuth>
                            } />
                            <Route path='/procedureInsights' element={
                                <RequireAuth>
                                    <ProcedureInsights />
                                </RequireAuth>
                            } />
                            <Route path='/procedures/:procedureId/analysis' element={
                                <RequireSuperAccess>
                                    <ProcedureAnalysis />
                                </RequireSuperAccess>
                            } />
                            <Route path='/procedures/:procedureId/items/:itemId' element={
                                <RequireSuperAccess>
                                    <ProcedureItemAnalysis />
                                </RequireSuperAccess>
                            } />
                            <Route path='/campaignMonitoring/campaigns' element={
                                <RequireAuth>
                                    <CampaignMonitoring />
                                </RequireAuth>
                            } />
                            <Route path='/campaignMonitoring/packs' element={
                                <RequireAuth>
                                    <PackMonitoring />
                                </RequireAuth>
                            } />
                            <Route path='/implementerInbox' element={
                                <RequireAuth>
                                    <ImplementerInbox />
                                </RequireAuth>
                            } />
                            <Route path='/implementerInbox/:draftId' element={
                                <RequireAuth>
                                    <ImplementerReview />
                                </RequireAuth>
                            } />
                            <Route path='/contracts' element={
                                <RequireSuperAccess>
                                    <ContractIntelligence />
                                </RequireSuperAccess>
                            } />
                            <Route path='/contracts/:contractId' element={
                                <RequireSuperAccess>
                                    <Contract />
                                </RequireSuperAccess>
                            } />
                            <Route path='/contracts/create' element={
                                <RequireSuperAccess>
                                    <CreateContract />
                                </RequireSuperAccess>
                            } />
                            <Route path='/physicianTexting' element={
                                <RequireSuperAccess>
                                    <PhysicianTexting />
                                </RequireSuperAccess>
                            } />
                            <Route path='/physicians/:physicianId/physicianTexting' element={
                                <RequireSuperAccess>
                                    <Conversation />
                                </RequireSuperAccess>
                            } />
                            <Route path='*' element={<Navigate to='/cards' replace />} />
                        </Route>
                        <Route element={
                            <RequireAuth>
                                <ApiContextProvider area="admin">
                                    <SuperSettingsLayout />
                                </ApiContextProvider>
                            </RequireAuth>
                        }>
                            <Route path='/superSettings/organizations' element={
                                <RequireSuperAccess>
                                    <SuperView />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId' element={
                                <RequireSuperAccess>
                                    <OrganizationView />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/create' element={
                                <RequireSuperAccess>
                                    <OrganizationConfiguration isCreating={true} />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/users' element={
                                <RequireSuperAccess>
                                    <UserManagement />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId' element={
                                <RequireSuperAccess>
                                    <SuperSettings/>
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/create' element={
                                <RequireSuperAccess>
                                    <CreateHospital/>
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/manageHospital' element={
                                <RequireSuperAccess>
                                    <UpdateHospitalSetup/>
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/passwordResets' element={
                                <RequireSuperAccess>
                                    <PasswordReset />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/manageCategories' element={
                                <RequireSuperAccess>
                                    <ManageCategories/>
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/imports' element={
                                <RequireSuperAccess>
                                    <Ingestion />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/imports/:importId' element={
                                <RequireSuperAccess>
                                    <IngestionImport />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/hospitals/:hospitalId/reviewerManagement' element={
                                <RequireSuperAccess>
                                    <ReviewerAssignmentPage />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/itemImageReview' element={
                                <RequireSuperAccess>
                                    <ItemImageReview />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/physicianContacts' element={
                                <RequireSuperAccess>
                                    <PhoneNumberUpload />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/physicians' element={
                                <RequireSuperAccess>
                                    <Physicians />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/physicians/:physicianId' element={
                                <RequireSuperAccess>
                                    <EditPhysician />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/data-exploration-agent' element={
                                <RequireSuperAccess>
                                    <DataExplorationAgent />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/recommendationBatches' element={
                                <RequireSuperAccess>
                                    <RecommendationBatchTracking />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/packs' element={
                                <RequireSuperAccess>
                                    <PackUsageManagement />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/manualUpload' element={
                                <RequireSuperAccess>
                                    <UploadSourceFile />
                                </RequireSuperAccess>
                            } />
                            <Route path='/superSettings/organizations/:organizationId/uncategorizedItemsReport' element={
                                <RequireSuperAccess>
                                    <UncategorizedItemsReportUpload />
                                </RequireSuperAccess>
                            } />
                        </Route>
                    </Routes>
                </ThemeProvider>
            </UserContextProvider>
        </AuthProvider>
    )
}

function Layout() {
    const drawerWidth = 250;
    const toolbarHeight = 64
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#f7f9fc' }}>
            <CssBaseline />
            <Header drawerWidth={drawerWidth} setOpenSidebar={setOpen} />
            <Sidebar drawerWidth={drawerWidth} open={open} setOpen={setOpen} />
            <Box sx={{ width: '100%', height: '100%', overflowY: 'scroll' }}>
                <Toolbar />
                <Box component='main' sx={{ width: '100%', height: `calc(100% - ${toolbarHeight}px)` }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

function SuperSettingsLayout() {
    const expandedWidth = 250;
    const collapsedWidth = 60;
    const [open, setOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const drawerWidth = sidebarCollapsed ? collapsedWidth : expandedWidth;

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#f7f9fc' }}>
            <CssBaseline />
            <Header drawerWidth={drawerWidth} setOpenSidebar={setOpen} />
            <Sidebar
                isSuperSettings={true}
                drawerWidth={drawerWidth}
                open={open}
                setOpen={setOpen}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
            />
            <Box sx={{ width: '100%', height: '100%', overflowY: 'scroll' }}>
                <Toolbar />
                <Box component='main' sx={{ width: '100%', height: 'calc(100% - 64px)' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

function DashboardLayout() {
    const { dashboardUser: physician, setDashboardUser } = usePhysicianDashboardUser();
    const [updateCardOwner] = useUpdateCardOwnerMutation();

    useEffect(() => {
        if (physician && !physician.privacy_policy_accepted_at) {
            updateCardOwner({
                cardOwnerId: physician.id,
                requestBody: { privacy_policy_accepted_at: new Date().toISOString() }
            }).then(response => {
                if (response.data) {
                    setDashboardUser(response.data);
                }
            });
        }
    }, []);

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', backgroundColor: '#EDEDED' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
                <Box component='main' sx={{ width: '100%', height: '100%' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

function LandingPageLayout() {
    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', backgroundColor: '#FFFFFF' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
                <Box component='main' sx={{ width: '100%', height: '100%' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

function StaffReviewLayout() {
    const drawerWidth = 250;
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#f7f9fc' }}>
            <CssBaseline />
            <Header drawerWidth={drawerWidth} setOpenSidebar={setOpen} />
            <StaffReviewSidebar drawerWidth={drawerWidth} open={open} setOpen={setOpen} />
            <Box sx={{ width: '100%', height: '100%', overflowY: 'scroll' }}>
                <Toolbar />
                <Box component='main' sx={{ width: '100%', height: 'calc(100% - 64px)' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default App;
